import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { AlertTriangle, CalendarDays, ArrowRight, Undo2 } from "lucide-react";

import { fmtDateStr } from "../lib/datetime";

const RESIGNATION_REASONS = [
  "Better Career Opportunity", "Higher Education", "Relocation",
  "Personal Reasons", "Family Reasons", "Compensation & Benefits",
  "Work Environment", "Management Issues", "Health Reasons",
  "Entrepreneurship", "Other",
];

// Exit interview questionnaire — keys are stable for storage.
const EXIT_INTERVIEW_SCHEMA = [
  { section: "Career & Growth", questions: [
    { key: "primary_reason", label: "What is the primary reason for leaving the organization?", type: "text" },
    { key: "decision_influencer", label: "What influenced your decision the most?", type: "text" },
    { key: "growth_adequate", label: "Did you receive adequate growth opportunities?", type: "yesno" },
    { key: "career_goals_discussed", label: "Were your career goals discussed regularly with your manager?", type: "yesno" },
    { key: "learning_opportunities", label: "Did the company provide sufficient learning and development opportunities?", type: "yesno" },
  ]},
  { section: "Manager & Leadership", questions: [
    { key: "manager_relationship", label: "How would you rate your relationship with your reporting manager?", type: "rating" },
    { key: "manager_feedback_regular", label: "Did you receive regular feedback from your manager?", type: "yesno" },
    { key: "manager_supportive", label: "Did you feel supported by your manager?", type: "yesno" },
    { key: "manager_could_do_diff", label: "What could your manager have done differently?", type: "text" },
  ]},
  { section: "Compensation & Benefits", questions: [
    { key: "comp_satisfied", label: "Were you satisfied with your compensation package?", type: "yesno" },
    { key: "benefits_satisfied", label: "Were you satisfied with employee benefits?", type: "yesno" },
    { key: "comp_major_reason", label: "Was compensation a major reason for leaving?", type: "yesno" },
  ]},
  { section: "Work Environment", questions: [
    { key: "culture_rating", label: "How would you rate the work culture?", type: "rating" },
    { key: "felt_respected", label: "Did you feel respected and valued?", type: "yesno" },
    { key: "policies_fair", label: "Were company policies fair and transparent?", type: "yesno" },
    { key: "workplace_issues", label: "Did you experience any workplace issues?", type: "text" },
    { key: "had_resources", label: "Did you have the resources needed to perform your job effectively?", type: "yesno" },
  ]},
  { section: "Team & Collaboration", questions: [
    { key: "teamwork_rating", label: "How would you rate teamwork within your department?", type: "rating" },
    { key: "communication_effective", label: "Were communication channels effective?", type: "yesno" },
    { key: "team_challenges", label: "Did you face any challenges while working with your team?", type: "text" },
  ]},
  { section: "Employee Experience", questions: [
    { key: "liked_most", label: "What did you like most about working here?", type: "text" },
    { key: "liked_least", label: "What did you like least about working here?", type: "text" },
    { key: "improvements", label: "What improvements would you recommend?", type: "text" },
    { key: "could_have_retained", label: "Is there anything the company could have done to retain you?", type: "text" },
  ]},
  { section: "Future Relationship", questions: [
    { key: "recommend_to_others", label: "Would you recommend this company to others?", type: "yesno" },
    { key: "rejoin_in_future", label: "Would you consider rejoining the company in the future?", type: "yesno" },
    { key: "open_to_consulting", label: "Would you be willing to work with us as a consultant/freelancer?", type: "yesno" },
    { key: "future_contact_ok", label: "Can we contact you in the future regarding opportunities?", type: "yesno" },
  ]},
  { section: "Final Feedback", questions: [
    { key: "additional_comments", label: "Any additional comments or suggestions?", type: "text" },
  ]},
];
const RATINGS = ["Excellent", "Good", "Average", "Poor"];

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + Number(n || 0));
  return d.toISOString().slice(0, 10);
};

export default function Resignation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  // basic form
  const [form, setForm] = useState({
    employment_type: user?.employment_type || "",
    job_type: user?.job_type || "",
    serve_notice: true,
    requested_notice_days: 30,
    no_notice_reason: "",
    proposed_last_working_day: addDays(30),
    reason_category: "",
    detailed_reason: "",
  });

  // Keep proposed_last_working_day in sync with requested_notice_days whenever the user
  // is serving notice. If they switch off "serve notice", we clear the auto-derived date.
  useEffect(() => {
    setForm((f) => {
      if (!f.serve_notice) return f;
      const lwd = addDays(f.requested_notice_days);
      if (lwd === f.proposed_last_working_day) return f;
      return { ...f, proposed_last_working_day: lwd };
    });
  }, [form.serve_notice, form.requested_notice_days]);
  const [saving, setSaving] = useState(false);

  // exit interview
  const [answers, setAnswers] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/my-resignation").then(({ data }) => {
      setActive(data || null);
      if (data?.exit_interview) setAnswers(data.exit_interview);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submitBasic = async () => {
    if (!form.reason_category) { toast.error("Pick a reason for resignation"); return; }
    if (!form.detailed_reason.trim()) { toast.error("Detailed reason is required"); return; }
    if (!form.proposed_last_working_day) { toast.error("Proposed last working day is required"); return; }
    if (form.serve_notice && Number(form.requested_notice_days) <= 0) { toast.error("Notice days must be > 0"); return; }
    if (!form.serve_notice && !form.no_notice_reason.trim()) { toast.error("Add a reason for not serving notice"); return; }
    setSaving(true);
    try {
      await api.post("/resignations", { ...form, requested_notice_days: Number(form.requested_notice_days) });
      toast.success("Resignation submitted");
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };

  // Flat list of all exit-interview question keys for completeness validation.
  const allExitKeys = EXIT_INTERVIEW_SCHEMA.flatMap((s) => s.questions.map((q) => q.key));
  const findMissingKeys = (a) => allExitKeys.filter((k) => {
    const v = a?.[k];
    return v == null || (typeof v === "string" && v.trim() === "");
  });

  const submitExitInterview = async () => {
    if (!active?.id) return;
    const missing = findMissingKeys(answers);
    if (missing.length > 0) {
      toast.error(`Please answer all ${allExitKeys.length} questions — ${missing.length} still pending.`);
      // Scroll/focus the first missing field for clarity.
      const el = document.querySelector(`[data-exit-key="${missing[0]}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/resignations/${active.id}/exit-interview`, { answers });
      toast.success("Exit interview saved");
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };

  const withdraw = async () => {
    if (!active?.id) return;
    try {
      await api.post(`/resignations/${active.id}/withdraw`);
      toast.success("Resignation withdrawn");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>;
  }

  // --- Active resignation (status view) ---
  if (active) {
    const statusChip = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-emerald-100 text-emerald-800",
    }[active.status] || "bg-slate-100";
    return (
      <div className="max-w-[1100px] space-y-6">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Offboarding</div>
            <h1 className="heading text-4xl font-extrabold tracking-tight text-slate-900">My Resignation</h1>
            <p className="text-slate-600 text-sm mt-1.5">Track your offboarding status and complete your exit interview.</p>
          </div>
          {active.status === "pending" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="resign-withdraw-btn">
                  <Undo2 size={14} className="mr-1.5" /> Withdraw Resignation
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Withdraw your resignation?</AlertDialogTitle>
                  <AlertDialogDescription>Your resignation request will be withdrawn. You can re-submit later if you change your mind.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep Resignation</AlertDialogCancel>
                  <AlertDialogAction onClick={withdraw} className="bg-amber-600 hover:bg-amber-700" data-testid="resign-withdraw-confirm">Yes, Withdraw</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        <div className="pk-card p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={statusChip + " uppercase tracking-wider"}>{active.status}</Badge>
              <span className="text-[12px] text-slate-500">Submitted {fmtDateStr(active.date_of_submission)}</span>
            </div>
            {active.status === "approved" && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Last Working Day</div>
                <div className="mono font-extrabold text-lg text-emerald-700">{fmtDateStr(active.approved_last_working_day)}</div>
                <div className="text-[11px] text-slate-500">{active.days_remaining} days remaining</div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
            <KV k="Reason" v={active.reason_category} />
            <KV k="Proposed Last Working Day" v={fmtDateStr(active.proposed_last_working_day)} mono />
            {active.status === "approved" && <KV k="Approved Notice Days" v={String(active.approved_notice_days)} mono />}
            <KV k="Employment Type" v={active.employment_type} />
            <KV k="Job Type" v={active.job_type} />
            <KV k="Date of Joining" v={fmtDateStr(active.date_of_joining)} mono />
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Detailed Reason</div>
              <div className="text-slate-700 mt-1">{active.detailed_reason}</div>
            </div>
            {active.decision_remark && (
              <div className="sm:col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">HR / Admin Note</div>
                <div className="text-slate-700 mt-1 italic">{active.decision_remark}</div>
              </div>
            )}
          </div>
        </div>

        {/* Exit interview (Step 2) */}
        <div className="pk-card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="pk-table-header">Step 2 · Exit Interview</div>
              <div className="text-[12px] text-slate-500 mt-0.5">
                Help us improve — your responses are confidential to HR.
                <span className="text-rose-600 font-semibold ml-1">All questions are mandatory.</span>
              </div>
            </div>
            {active.exit_interview_submitted ? (
              <Badge className="bg-emerald-100 text-emerald-800">Submitted</Badge>
            ) : (
              <Badge className={findMissingKeys(answers).length === 0 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid="exit-progress-chip">
                {allExitKeys.length - findMissingKeys(answers).length} / {allExitKeys.length} answered
              </Badge>
            )}
          </div>
          <ExitInterviewForm answers={answers} setAnswers={setAnswers} disabled={!!active.exit_interview_submitted} />
          {!active.exit_interview_submitted && (
            <div className="flex items-center justify-end gap-3 mt-4">
              {findMissingKeys(answers).length > 0 && (
                <span className="text-[11.5px] text-amber-700 font-semibold" data-testid="exit-pending-text">
                  {findMissingKeys(answers).length} question{findMissingKeys(answers).length === 1 ? "" : "s"} pending
                </span>
              )}
              <Button onClick={submitExitInterview} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="resign-exit-submit">
                {saving ? "Saving…" : "Submit Exit Interview"}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- No active → show the basic submission form ---
  return (
    <div className="max-w-[900px] space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Offboarding</div>
        <h1 className="heading text-4xl font-extrabold tracking-tight text-slate-900">Resignation Form</h1>
        <p className="text-slate-600 text-sm mt-1.5">Submit your resignation. Once you submit, HR / Master Admin will review and confirm your last working day.</p>
      </div>

      <div className="pk-card p-5 bg-rose-50/40 border-rose-200 flex items-start gap-3" data-testid="resign-warning">
        <AlertTriangle size={20} className="text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="text-[13px] text-rose-800">
          <div className="font-semibold mb-0.5">This action initiates your offboarding.</div>
          <div className="text-rose-700">You may withdraw your request until HR/Master Admin approves it. After approval, this becomes <span className="font-semibold">irreversible</span>.</div>
        </div>
      </div>

      <div className="pk-card p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <KV k="Name" v={user?.name} />
          <KV k="Employee Code" v={user?.employee_code} mono />
          <KV k="Date of Joining" v={user?.date_of_joining || "—"} mono />
          <KV k="Date of Submission" v={today()} mono />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-slate-100 pt-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Employment Type · (auto)</Label>
            <Input
              value={form.employment_type || "—"}
              readOnly
              className="mt-1.5 bg-slate-50 border-slate-200 font-semibold text-slate-700 cursor-not-allowed"
              data-testid="resign-employment-type"
            />
            {!form.employment_type && <div className="text-[10.5px] text-amber-700 mt-1">Not set in your profile — ask HR to update it.</div>}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Job Type · (auto)</Label>
            <Input
              value={form.job_type || "—"}
              readOnly
              className="mt-1.5 bg-slate-50 border-slate-200 font-semibold text-slate-700 cursor-not-allowed"
              data-testid="resign-job-type"
            />
            {!form.job_type && <div className="text-[10.5px] text-amber-700 mt-1">Not set in your profile — ask HR to update it.</div>}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4 space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={form.serve_notice} onCheckedChange={(v) => setForm({ ...form, serve_notice: v })} data-testid="resign-serve-notice" />
            <span className="text-[13px] font-medium text-slate-700">Do you want to serve the notice period?</span>
            <Badge className={form.serve_notice ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>{form.serve_notice ? "Yes" : "No"}</Badge>
          </div>
          {form.serve_notice ? (
            <div>
              <Label>Minimum Notice Days You Can Serve *</Label>
              <Input type="number" min="1" value={form.requested_notice_days} onChange={(e) => setForm({ ...form, requested_notice_days: e.target.value })} className="mt-1.5 mono w-40" data-testid="resign-notice-days" />
              <div className="text-[10.5px] text-slate-500 mt-1">HR / Master Admin will confirm the final number of notice days upon approval.</div>
            </div>
          ) : (
            <div>
              <Label>Reason for not serving notice *</Label>
              <Textarea rows={2} value={form.no_notice_reason} onChange={(e) => setForm({ ...form, no_notice_reason: e.target.value })} className="mt-1.5" placeholder="e.g. urgent personal commitment…" data-testid="resign-no-notice-reason" />
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Proposed Last Working Day {form.serve_notice ? "· (auto)" : "*"}</Label>
            <Input
              type="date"
              value={fmtDateStr(form.proposed_last_working_day)}
              onChange={(e) => setForm({ ...form, proposed_last_working_day: e.target.value })}
              readOnly={form.serve_notice}
              className={`mt-1.5 ${form.serve_notice ? "bg-slate-50 cursor-not-allowed font-semibold" : ""}`}
              data-testid="resign-proposed-lwd"
            />
            <div className="text-[10.5px] text-slate-500 mt-1">
              {form.serve_notice
                ? `Auto-calculated from today + ${form.requested_notice_days || 0} notice days. HR / Master Admin will finalize.`
                : "HR / Master Admin will finalize this date."}
            </div>
          </div>
          <div>
            <Label>Reason for Resignation *</Label>
            <Select value={form.reason_category} onValueChange={(v) => setForm({ ...form, reason_category: v })}>
              <SelectTrigger className="mt-1.5" data-testid="resign-reason"><SelectValue placeholder="Pick a reason…" /></SelectTrigger>
              <SelectContent>{RESIGNATION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Detailed Reason for Leaving *</Label>
          <Textarea rows={4} value={form.detailed_reason} onChange={(e) => setForm({ ...form, detailed_reason: e.target.value })} className="mt-1.5" placeholder="Describe your reasons in detail…" data-testid="resign-detailed-reason" />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/")} disabled={saving}>Cancel</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={saving} data-testid="resign-submit-btn">
              Submit Resignation <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="text-rose-600" size={20} /> Submit your resignation?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2 pt-1">
                <span className="block">Once you submit, HR / Master Admin will start reviewing.</span>
                <span className="block font-semibold text-rose-700">You can withdraw before approval — but after approval, this cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={submitBasic} className="bg-rose-600 hover:bg-rose-700" data-testid="resign-submit-confirm">Yes, Submit</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}

function ExitInterviewForm({ answers, setAnswers, disabled }) {
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  return (
    <div className="space-y-5">
      {EXIT_INTERVIEW_SCHEMA.map((sec) => (
        <div key={sec.section}>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 mb-2 flex items-center gap-1.5">
            <CalendarDays size={12} /> {sec.section}
          </div>
          <div className="space-y-3 pl-4 border-l-2 border-violet-100">
            {sec.questions.map((q) => {
              const val = answers[q.key];
              const missing = val == null || (typeof val === "string" && val.trim() === "");
              return (
              <div key={q.key} data-exit-key={q.key}>
                <Label className={`text-[12px] font-medium ${missing && !disabled ? "text-rose-700" : "text-slate-700"}`}>
                  {q.label} <span className="text-rose-600">*</span>
                </Label>
                {q.type === "text" && (
                  <Textarea rows={2} value={answers[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} disabled={disabled} className={`mt-1.5 ${missing && !disabled ? "border-rose-300" : ""}`} data-testid={`exit-${q.key}`} />
                )}
                {q.type === "yesno" && (
                  <div className="flex gap-3 mt-1.5">
                    {["Yes", "No"].map((opt) => (
                      <label key={opt} className={`flex items-center gap-1.5 px-3 py-1.5 rounded border cursor-pointer text-[12px] ${answers[q.key] === opt ? "bg-violet-50 border-violet-400 font-semibold text-violet-800" : (missing && !disabled ? "bg-white border-rose-300 text-slate-700" : "bg-white border-slate-200 text-slate-700")} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                        <input type="radio" name={q.key} value={opt} checked={answers[q.key] === opt} onChange={() => !disabled && set(q.key, opt)} className="sr-only" data-testid={`exit-${q.key}-${opt}`} />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}
                {q.type === "rating" && (
                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {RATINGS.map((opt) => (
                      <label key={opt} className={`px-3 py-1.5 rounded border cursor-pointer text-[12px] ${answers[q.key] === opt ? "bg-violet-50 border-violet-400 font-semibold text-violet-800" : (missing && !disabled ? "bg-white border-rose-300 text-slate-700" : "bg-white border-slate-200 text-slate-700")} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                        <input type="radio" name={q.key} value={opt} checked={answers[q.key] === opt} onChange={() => !disabled && set(q.key, opt)} className="sr-only" data-testid={`exit-${q.key}-${opt}`} />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
